import { BarChart3 } from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { isPlaceholder } from '../data/transform'
import type { CrmDataset, Organization } from '../types/crm'
import { formatCurrency, formatPercent } from '../utils/format'

const PRIORITY_ORDER = [
  'A+',
  'A',
  'B',
  'C',
  'D',
]

function hasWebsite(
  organization: Organization,
): boolean {
  return (
    !isPlaceholder(organization.website) &&
    !/does not have/i.test(
      organization.website,
    )
  )
}

function hasEmail(
  organization: Organization,
): boolean {
  return !isPlaceholder(
    organization.email,
  )
}

function hasPhone(
  organization: Organization,
): boolean {
  return !isPlaceholder(
    organization.phone,
  )
}

function hasLocation(
  organization: Organization,
): boolean {
  return (
    !isPlaceholder(
      organization.city,
    ) &&
    !isPlaceholder(
      organization.state,
    )
  )
}

function priorityRank(
  priority: string,
): number {
  const index =
    PRIORITY_ORDER.indexOf(priority)

  return index >= 0
    ? index
    : PRIORITY_ORDER.length
}

export function AnalyticsPage({
  dataset,
}: {
  dataset: CrmDataset
}) {
  const { metrics } = dataset

  const ownerCoverage =
    metrics.totalLeads > 0
      ? metrics.ownersIdentified /
        metrics.totalLeads
      : 0

  const websiteCoverage =
    dataset.leads.filter(
      (lead) =>
        lead.organization &&
        hasWebsite(lead.organization),
    ).length

  const emailCoverage =
    dataset.leads.filter(
      (lead) =>
        lead.organization &&
        hasEmail(lead.organization),
    ).length

  const phoneCoverage =
    dataset.leads.filter(
      (lead) =>
        lead.organization &&
        hasPhone(lead.organization),
    ).length

  const locationCoverage =
    dataset.leads.filter(
      (lead) =>
        lead.organization &&
        hasLocation(lead.organization),
    ).length

  const completeProspects =
    dataset.leads.filter(
      (lead) => {
        const organization =
          lead.organization

        if (
          !lead.ownerIdentified ||
          !organization
        ) {
          return false
        }

        const hasContact =
          hasEmail(organization) ||
          hasPhone(organization)

        return (
          hasContact &&
          hasWebsite(organization) &&
          hasLocation(organization)
        )
      },
    ).length

  const completeCoverage =
    metrics.totalLeads > 0
      ? completeProspects /
        metrics.totalLeads
      : 0

  const priorityMap =
    new Map<
      string,
      {
        priority: string
        count: number
        value: number
      }
    >()

  for (
    const opportunity
    of dataset.opportunities
  ) {
    const priority =
      opportunity.priority.trim() ||
      'Unassigned'

    const current =
      priorityMap.get(priority) ?? {
        priority,
        count: 0,
        value: 0,
      }

    current.count += 1
    current.value +=
      Number.isFinite(
        opportunity.value,
      )
        ? opportunity.value
        : 0

    priorityMap.set(
      priority,
      current,
    )
  }

  const priorityRows =
    Array.from(
      priorityMap.values(),
    ).sort(
      (left, right) => {
        const rankDifference =
          priorityRank(left.priority) -
          priorityRank(right.priority)

        if (rankDifference !== 0) {
          return rankDifference
        }

        return left.priority.localeCompare(
          right.priority,
        )
      },
    )

  const maxPriorityValue =
    Math.max(
      0,
      ...priorityRows.map(
        (row) => row.value,
      ),
    )

  const healthRows = [
    {
      label: 'Owners',
      count: metrics.ownersIdentified,
      total: metrics.totalLeads,
    },
    {
      label: 'Phone',
      count: phoneCoverage,
      total: metrics.totalLeads,
    },
    {
      label: 'Email',
      count: emailCoverage,
      total: metrics.totalLeads,
    },
    {
      label: 'Website',
      count: websiteCoverage,
      total: metrics.totalLeads,
    },
    {
      label: 'Location',
      count: locationCoverage,
      total: metrics.totalLeads,
    },
  ]

  const activeStages =
    metrics.pipelineStages.filter(
      (stage) => stage.count > 0,
    )

  const stageTotal =
    Math.max(
      1,
      ...activeStages.map(
        (stage) => stage.count,
      ),
    )

  return (
    <div className="page analytics-page">
      <PageHeader
        title="Analytics"
        description="Pipeline health and CRM data quality from the live workbook."
      />

      <section
        aria-label="Pipeline summary"
        className="analytics-summary"
      >
        <div>
          <span>
            Open pipeline
          </span>

          <strong>
            {formatCurrency(
              metrics.pipelineValue,
            )}
          </strong>

          <small>
            {
              metrics.openOpportunities
            } open opportunities
          </small>
        </div>

        <div>
          <span>
            Owner coverage
          </span>

          <strong>
            {formatPercent(
              ownerCoverage,
            )}
          </strong>

          <small>
            {
              metrics.ownersIdentified
            } of {metrics.totalLeads} leads
          </small>
        </div>

        <div>
          <span>
            Complete prospects
          </span>

          <strong>
            {completeProspects}
            {' / '}
            {metrics.totalLeads}
          </strong>

          <small>
            {formatPercent(
              completeCoverage,
            )} ready to work
          </small>
        </div>
      </section>

      <div className="analytics-grid executive-analytics-grid">
        <section className="panel analytics-panel">
          <header className="panel-header">
            <div>
              <h2>
                Pipeline by priority
              </h2>

              <p>
                Where current opportunity
                value is concentrated.
              </p>
            </div>
          </header>

          {priorityRows.length > 0 ? (
            <div className="priority-analytics-list">
              {priorityRows.map(
                (row) => {
                  const width =
                    maxPriorityValue > 0
                      ? (
                        row.value /
                        maxPriorityValue
                      ) * 100
                      : 0

                  return (
                    <div
                      className="priority-analytics-row"
                      key={row.priority}
                    >
                      <div className="priority-analytics-label">
                        <strong>
                          {row.priority}
                        </strong>

                        <span>
                          {row.count}{' '}
                          {row.count === 1
                            ? 'opportunity'
                            : 'opportunities'}
                        </span>
                      </div>

                      <div
                        aria-hidden="true"
                        className="priority-analytics-track"
                      >
                        <span
                          style={{
                            width:
                              `${width}%`,
                          }}
                        />
                      </div>

                      <strong className="priority-analytics-value">
                        {formatCurrency(
                          row.value,
                        )}
                      </strong>
                    </div>
                  )
                },
              )}
            </div>
          ) : (
            <div className="analytics-empty-state">
              No opportunity priority
              data is available yet.
            </div>
          )}
        </section>

        <section className="panel analytics-panel">
          <header className="panel-header">
            <div>
              <h2>
                Data health
              </h2>

              <p>
                How usable the current
                prospect records are.
              </p>
            </div>
          </header>

          <div className="analytics-health-list">
            {healthRows.map(
              (row) => {
                const ratio =
                  row.total > 0
                    ? row.count /
                      row.total
                    : 0

                return (
                  <div
                    className="analytics-health-row"
                    key={row.label}
                  >
                    <div className="analytics-health-heading">
                      <span>
                        {row.label}
                      </span>

                      <strong>
                        {row.count}
                        {' / '}
                        {row.total}
                      </strong>
                    </div>

                    <div className="analytics-health-bottom">
                      <div
                        aria-hidden="true"
                        className="analytics-health-track"
                      >
                        <span
                          style={{
                            width:
                              `${ratio * 100}%`,
                          }}
                        />
                      </div>

                      <strong>
                        {formatPercent(
                          ratio,
                        )}
                      </strong>
                    </div>
                  </div>
                )
              },
            )}
          </div>
        </section>
      </div>

      <section className="panel analytics-panel analytics-stage-panel">
        <header className="panel-header">
          <div>
            <h2>
              Pipeline stages
            </h2>

            <p>
              Real stage distribution
              from current opportunities.
            </p>
          </div>
        </header>

        {activeStages.length > 1 ? (
          <div className="analytics-stage-list">
            {activeStages.map(
              (stage) => (
                <div
                  className="analytics-stage-row"
                  key={stage.stage}
                >
                  <div>
                    <strong>
                      {stage.stage}
                    </strong>

                    <span>
                      {stage.count}{' '}
                      {stage.count === 1
                        ? 'opportunity'
                        : 'opportunities'}
                    </span>
                  </div>

                  <div
                    aria-hidden="true"
                    className="analytics-stage-track"
                  >
                    <span
                      style={{
                        width:
                          `${
                            (
                              stage.count /
                              stageTotal
                            ) * 100
                          }%`,
                      }}
                    />
                  </div>

                  <strong>
                    {formatCurrency(
                      stage.value,
                    )}
                  </strong>
                </div>
              ),
            )}
          </div>
        ) : (
          <div className="analytics-stage-status">
            <div className="analytics-stage-status-icon">
              <BarChart3
                aria-hidden="true"
                size={19}
              />
            </div>

            <div>
              <strong>
                {activeStages.length === 1
                  ? `All ${
                      activeStages[0].count
                    } opportunities are currently in ${
                      activeStages[0].stage
                    }.`
                  : 'No pipeline stage data is available yet.'}
              </strong>

              <p>
                Stage analytics will become
                more useful as opportunities
                move through the pipeline.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

